import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

class BundleQuantityPicker extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.addEventListener('change', this.#handleBundleChange);
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);

    // The bundle can be rendered before the product form has finished parsing.
    // Sync again on the next frame so this also works without a quantity block.
    this.#syncSelectedQuantity();
    requestAnimationFrame(() => {
      if (this.isConnected) this.#syncSelectedQuantity();
    });

    customElements.whenDefined('quantity-selector-component').then(() => {
      if (this.isConnected) this.#syncSelectedQuantity();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleBundleChange);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  #handleBundleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-bundle-quantity]')) return;
    this.#setProductQuantity(Number.parseInt(event.target.value, 10));
  };

  #syncSelectedQuantity() {
    const selectedBundle = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-bundle-quantity]:checked')
    );
    if (selectedBundle) this.#setProductQuantity(Number.parseInt(selectedBundle.value, 10));
  }

  /**
   * @param {number} quantity
   */
  #setProductQuantity(quantity) {
    if (!Number.isInteger(quantity) || quantity < 1) return;

    const productForm = this.#getProductForm();
    if (!productForm) return;

    const quantitySelector = this.#getQuantitySelector();
    if (quantitySelector) {
      productForm.querySelector('[data-bundle-quantity-input]')?.remove();
      quantitySelector.setValue?.(quantity.toString());
      quantitySelector.updateButtonStates?.();
      quantitySelector.onQuantityChange?.();
      return;
    }

    const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;

    let quantityInput = form.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.setAttribute('data-bundle-quantity-input', '');
      form.append(quantityInput);
    }

    quantityInput.value = quantity.toString();
  }

  #getProductForm() {
    const productDetails = this.closest('.product-details');
    return productDetails?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`
    ) ?? null;
  }

  #getQuantitySelector() {
    return this.#getProductForm()?.querySelector('quantity-selector-component') ?? null;
  }

  #handleQuantityUpdate = (event) => {
    const quantitySelector = this.#getQuantitySelector();
    if (!quantitySelector || event.target !== quantitySelector) return;

    const quantity = Number(event.detail?.quantity);
    const matchingBundle = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[data-bundle-quantity][value="${quantity}"]`)
    );

    this.querySelectorAll('[data-bundle-quantity]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = input === matchingBundle;
    });
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId && !detail.newProduct) return;

        const updatedPicker = detail.html.querySelector(
          `bundle-quantity-picker[data-block-id="${this.dataset.blockId}"]`
        );
        if (!updatedPicker) return;

        const currentPrices = this.querySelectorAll('[data-bundle-price]');
        const updatedPrices = updatedPicker.querySelectorAll('[data-bundle-price]');

        currentPrices.forEach((price, index) => {
          const updatedPrice = updatedPrices[index];
          if (updatedPrice) price.replaceChildren(...updatedPrice.cloneNode(true).childNodes);
        });

        if (detail.newProduct?.id) this.dataset.productId = detail.newProduct.id;
        this.#syncSelectedQuantity();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-quantity-picker] Update failed:', error);
      });
  };
}

if (!customElements.get('bundle-quantity-picker')) {
  customElements.define('bundle-quantity-picker', BundleQuantityPicker);
}
